import { useState, Dispatch, SetStateAction } from "react";
import { useToast } from "ui-kit";
import client from "libs/client";
import type { UserNoteResponse } from "libs/apiClient";
import { _ } from "i18n";
import "./admin-ui.scss";
import "./UserNoteForm.css";

interface UserNoteFormCreateProps {
	userId: number;
	setShowModal: Dispatch<SetStateAction<boolean>>;
	mode: "create";
}

interface UserNoteFormEditProps {
	note: UserNoteResponse;
	setShowModal: Dispatch<SetStateAction<boolean>>;
	mode: "edit";
}

type UserNoteFormProps = UserNoteFormCreateProps | UserNoteFormEditProps;

export function UserNoteForm(props: UserNoteFormProps) {
	const { addErrorToast, addSuccessToast } = useToast();
	const isEditMode = props.mode === "edit";
	const initialNote = isEditMode ? props.note.note : "";
	const [noteText, setNoteText] = useState<string>(initialNote);
	const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

	const handleNoteMutationError = (res: { status?: number; message: string }) => {
		setIsSubmitting(false);
		if (res.status !== 403) {
			addErrorToast(res.message);
		}
	};

	const postDiscoverUserNoteMutation = client.postAdminDiscoverUserNotes.useMutation({
		onSuccess: () => {
			addSuccessToast(_("discover.userNote.toast.created"));
			client.queryClient.invalidateQueries({
				predicate: (query) => query.queryKey.includes("discoverUserDetail"),
			});
			props.setShowModal(false);
			setNoteText("");
			setIsSubmitting(false);
		},
		onError: handleNoteMutationError,
	});

	const patchUserNoteMutation = client.patchAdminDiscoverUserNote.useMutation({
		onSuccess: () => {
			addSuccessToast(_("discover.userNote.toast.updated"));
			client.queryClient.invalidateQueries({
				predicate: (query) => query.queryKey.includes("discoverUserDetail"),
			});
			props.setShowModal(false);
			setIsSubmitting(false);
		},
		onError: handleNoteMutationError,
	});

	const handleSubmit = () => {
		if (noteText.trim().length === 0) {
			addErrorToast(_("discover.userNote.toast.cannotBeEmpty"));
			return;
		}

		setIsSubmitting(true);
		if (isEditMode) {
			patchUserNoteMutation.mutate({
				id: props.note.id,
				body: {
					note: noteText.trim(),
				},
			});
		} else {
			postDiscoverUserNoteMutation.mutate({
				body: {
					userId: props.userId,
					note: noteText.trim(),
				},
			});
		}
	};

	const isFormValid = noteText.trim().length > 0;
	const charCount = noteText.length;
	const textareaId = isEditMode ? "note-textarea-edit" : "note-textarea";
	const submitButtonText = isSubmitting
		? isEditMode
			? _("discover.userNote.updating")
			: _("discover.userNote.submitting")
		: isEditMode
			? _("discover.userNote.updateNote")
			: _("discover.userNote.submitNote");

	return (
		<div className="user-note-form">
			<label htmlFor={textareaId} className="user-note-form__label">
				{_("discover.userNote.label")}
			</label>
			<textarea
				id={textareaId}
				className="user-note-form__textarea"
				value={noteText}
				onChange={(e) => setNoteText(e.target.value)}
				rows={5}
				placeholder={_("discover.userNote.placeholder")}
				disabled={isSubmitting}
			/>
			<div className="user-note-form__char-count">
				{charCount} {charCount === 1 ? _("discover.userNote.character") : _("discover.userNote.characters")}
			</div>
			<div className="admin-buttons admin-buttons--right">
				<button className="admin-button admin-button--primary" disabled={!isFormValid || isSubmitting} onClick={handleSubmit}>
					{submitButtonText}
				</button>
				{isEditMode && (
					<button className="admin-button admin-button--secondary" disabled={isSubmitting} onClick={() => props.setShowModal(false)}>
						{_("btn.cancel")}
					</button>
				)}
			</div>
		</div>
	);
}
